/* eslint-disable react-refresh/only-export-components */
// 同文件导出组件 + 辅助导出为有意模式;react-refresh 仅关注 HMR,不影响运行时。
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, RotateCcw, RefreshCw } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/button";
import { logger } from "@/shared/lib/logger";
import { reportBoundaryError } from "@/shared/lib/errorReporting";

function FallbackUI() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="cyber-page fx-enter flex min-h-[60vh] items-center justify-center">
      <div className="text-center max-w-md">
        {/* V26 token 化 + fx 动效:warning 色徽章 + 持续 glow,绕环慢速旋转。 */}
        <div
          className="fx-glow relative mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full border border-warning/50 text-warning"
          style={{
            background:
              "radial-gradient(circle at center, hsl(var(--warning) / 0.2), hsl(var(--warning) / 0.05) 60%, transparent)",
            boxShadow:
              "0 0 0 1px hsl(var(--warning) / 0.35), 0 0 40px hsl(var(--warning) / 0.4), 0 0 80px hsl(var(--chart-3) / 0.25)",
          }}
        >
          <div className="pointer-events-none absolute -inset-2 rounded-full border border-dashed border-warning/45 fx-spin-slow" />
          <AlertTriangle className="h-9 w-9 drop-shadow-[0_0_10px_hsl(var(--warning)/0.9)]" />
        </div>
        <h2
          className="text-2xl font-bold uppercase tracking-[0.2em] text-gradient-hero"
          style={{ fontFamily: "Orbitron, Noto Sans SC, sans-serif" }}
        >
          {t("pageError.title")}
        </h2>
        <p className="mt-3 text-xs text-muted-foreground leading-relaxed tracking-wider uppercase">
          {t("pageError.subtitle")}
        </p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <Button variant="outline" onClick={() => navigate(-1)} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {t("pageError.back")}
          </Button>
          {/* 刷新通常能恢复:代客切换/权限刷新后页面拿到的瞬时空数据崩溃,reload 重取即好。 */}
          <Button onClick={() => window.location.reload()} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {t("pageError.reload")}
          </Button>
          <Button variant="outline" onClick={() => navigate("/")} className="gap-2">
            <RotateCcw className="h-4 w-4" />
            {t("pageError.home")}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class PageErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    logger.error("Page render error", {
      error: error.message,
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
    });
    reportBoundaryError(error, info.componentStack ?? undefined, "page");
  }

  render() {
    if (this.state.hasError) {
      return <FallbackUI />;
    }
    return this.props.children;
  }
}
